import React from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import Col from 'components/ui/Col';
import Row from 'components/ui/Row';

import { AntdSelect as Select, AntdButton as Button, DatePicker } from '@yuhuinc/ui-library/';

import { LeaseCreatorFormItem } from '../common/LeaseCreatorFormItem';
import { FormError } from '../common/FormError';
import { MoneyInput } from '../common/MoneyInput';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { validateRentableAssetForm } from './LeasePricingFormValidator';
import { getTranslatedString } from 'helpers/i18n';
import { responsive } from 'helpers/StyleHelper';
import { StyledButton } from '../common/LeaseStyles';

const FORM_NAME = 'rentable_item_form';

const StyledRow = styled(Row)`
  margin-bottom: ${(props) => props.mb || '21px'};
  margin-top: ${(props) => props.mt || '0px'};
`;

const SyledFormButtonContainer = styled.div`
  display: flex;
`;

const RentableItemForm = ({
  setToggleNewItem,
  getRentableAssets,
  getRentableAssetNumbers,
  property,
  validRentableItems,
  setValidRentableItemsInStore,
  selectedLeaseStartDate,
  selectedEarlyMoveInDate,
  selectedLeaseEndDate,
  itemForEdit,
  isEdit,
  isMobile,
  editIndex,
  resetAfterEdit,
}) => {
  const terms = Array(13)
    .fill()
    .map((_, index) => {
      if (index === 0) {
        return {
          label: getTranslatedString('landlordPortal.leaseCreatorV2.monthly'),
          value: 'monthly',
        };
      }
      let month = index + 1;
      let monthVerbiage = getTranslatedString('landlordPortal.leaseCreatorV2.months');
      if (month === 1) {
        monthVerbiage = getTranslatedString('landlordPortal.leaseCreatorV2.month');
      }
      return {
        label: `${month} ${monthVerbiage}`,
        value: month,
      };
    });

  const labelSize = isMobile ? responsive.text.mobile : responsive.text.desktop;

  return (
    <Formik
      initialValues={{
        asset_type: itemForEdit.asset_type || undefined,
        asset_id: itemForEdit.asset_id || undefined,
        monthly_rent: itemForEdit.monthly_rent || 0,
        deposit_amount: itemForEdit.deposit_amount || 0,
        key_deposit: itemForEdit.key_deposit || 0,
        start_date: itemForEdit.start_date || undefined,
        term_length: itemForEdit.term_length || undefined,
        end_date: itemForEdit.end_date || undefined,
      }}
      validationSchema={validateRentableAssetForm(selectedLeaseStartDate, selectedEarlyMoveInDate)}
      enableReinitialize={true}
      validateOnChange={false} // To only validate on submit
      validateOnBlur={false} // To only validate on submit
      onSubmit={(values) => {
        let newValidRentableItems = [...validRentableItems];
        if (isEdit) {
          newValidRentableItems[editIndex] = values;
          resetAfterEdit();
        } else {
          newValidRentableItems.push(values);
        }
        setValidRentableItemsInStore(newValidRentableItems);
        setToggleNewItem(false);
      }}
    >
      {({ handleSubmit, values, setFieldValue, errors, touched, setFieldTouched }) => {
        let rentableAssetOptions = getRentableAssets();
        let rentableAssetNumbers = getRentableAssetNumbers(
          values['asset_type'],
          isEdit,
          values['asset_id']
        );

        const updateEndDate = (startDate, termLength) => {
          if (!startDate || !termLength) {
            setFieldValue('end_date', undefined);
          } else if (termLength === 'monthly') {
            setFieldValue('end_date', selectedLeaseEndDate);
          } else {
            const endDate = startDate.add(termLength, 'months').format('YYYY-MM-DD');
            setFieldValue('end_date', endDate);
          }
        };

        const getRentableAsset = (assetNumber) => {
          if (isEmpty(property) || !assetNumber) {
            return undefined;
          }

          return property.ancillary_items.find((ancillaryItem) => ancillaryItem.id === assetNumber);
        };

        return (
          <VerticalFormWrapper id={FORM_NAME} onSubmit={handleSubmit}>
            {!isEmpty(errors) && (
              <StyledRow>
                <FormError errors={errors} />
              </StyledRow>
            )}
            <StyledRow gutter={16} mb="8px">
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.assetType'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                  labelCol={{ span: 3 }}
                  required
                >
                  <Select
                    id="asset_type"
                    name="asset_type"
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.selectAnAssetType'
                    )}
                    optionData={rentableAssetOptions}
                    handleChange={(value) => {
                      setFieldValue('asset_type', value);
                      // unset asset number, and asset on asset type change
                      setFieldValue('asset_id', undefined);
                      setFieldValue('monthly_rent', 0);
                      setFieldValue('deposit_amount', 0);
                      setFieldValue('key_deposit', 0);
                    }}
                    value={values['asset_type']}
                    error={errors.asset_type}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.assetNumber'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                  required
                >
                  <Select
                    id="asset_id"
                    name="asset_id"
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.selectAnAssetNumber'
                    )}
                    disabled={!values['asset_type']}
                    optionData={rentableAssetNumbers}
                    handleChange={(value) => {
                      setFieldValue('asset_id', value);
                      // fetch selected rentable asset for rent amounts
                      // and set values using selected asset
                      const selectedAsset = getRentableAsset(value);
                      setFieldValue('monthly_rent', selectedAsset.market_amount);
                      setFieldValue('deposit_amount', selectedAsset.market_deposit_amount);
                      setFieldValue('key_deposit', selectedAsset.market_key_deposit_amount);
                    }}
                    value={values['asset_id']}
                    error={errors.asset_id}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
            </StyledRow>

            <StyledRow gutter={16} mb="8px">
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.monthlyRent'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                  required
                >
                  <MoneyInput
                    id="monthly_rent"
                    name="monthly_rent"
                    // value={values['monthly_rent'] || asset?.market_amount}
                    value={values['monthly_rent']}
                    handleChange={(value) => {
                      setFieldTouched('monthly_rent');
                      if (value === '' || value === null) {
                        setFieldValue('monthly_rent', undefined);
                      } else {
                        setFieldValue('monthly_rent', value);
                      }
                    }}
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.enterAnAmount'
                    )}
                    error={errors.monthly_rent}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.rentalDeposit'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                  required
                >
                  <MoneyInput
                    id="deposit_amount"
                    name="deposit_amount"
                    value={values['deposit_amount']}
                    handleChange={(value) => {
                      setFieldTouched('deposit_amount');
                      if (value === '' || value === null) {
                        setFieldValue('deposit_amount', undefined);
                      } else {
                        setFieldValue('deposit_amount', value);
                      }
                    }}
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.enterAnAmount'
                    )}
                    error={errors.deposit_amount}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
            </StyledRow>

            <StyledRow gutter={16} mb="8px">
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.keyDeposit'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                  required
                >
                  <MoneyInput
                    id="key_deposit"
                    name="key_deposit"
                    value={values['key_deposit']}
                    handleChange={(value) => {
                      setFieldTouched('key_deposit');
                      if (value === '' || value === null) {
                        setFieldValue('key_deposit', undefined);
                      } else {
                        setFieldValue('key_deposit', value);
                      }
                    }}
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.enterAnAmount'
                    )}
                    error={errors.key_deposit}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.startDate'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                  required
                >
                  <DatePicker
                    id="start_date"
                    name="start_date"
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.selectADate'
                    )}
                    onChange={(date) => {
                      setFieldTouched('start_date', true);
                      if (date) {
                        setFieldValue('start_date', date.format('YYYY-MM-DD'));
                        updateEndDate(date, values.term_length);
                      } else {
                        setFieldValue('start_date', '');
                        updateEndDate(undefined, values.term_length);
                      }
                    }}
                    value={values['start_date'] ? moment(values['start_date']) : undefined}
                    error={errors.start_date}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
            </StyledRow>

            <StyledRow gutter={16} mb="16px">
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.termLength'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                  required
                >
                  <Select
                    id="term_length"
                    name="term_length"
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.selectATerm'
                    )}
                    optionData={terms}
                    handleChange={(value) => {
                      let startDate = values.start_date ? moment(values.start_date) : undefined;
                      setFieldValue('term_length', value);
                      updateEndDate(startDate, value);
                    }}
                    value={values['term_length']}
                    error={errors.term_length}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
              <Col md={12}>
                <LeaseCreatorFormItem
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePricingPage.endDate'
                  )}
                  labelSize={labelSize}
                  labelBottomMargin="-5px"
                >
                  <DatePicker
                    id="end_date"
                    name="end_date"
                    placeholder={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.endDate'
                    )}
                    disabled={true}
                    onChange={(date) =>
                      date
                        ? setFieldValue('end_date', date.format('YYYY-MM-DD'))
                        : setFieldValue('end_date', undefined)
                    }
                    value={values['end_date'] ? moment(values['end_date']) : undefined}
                    isMobile={isMobile}
                  />
                </LeaseCreatorFormItem>
              </Col>
            </StyledRow>

            <SyledFormButtonContainer>
              <StyledButton
                label={getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.cancel')}
                htmlType="button"
                onClick={() => {
                  if (isEdit) {
                    resetAfterEdit(); // unset any item stored in state when closing
                  }
                  setToggleNewItem(false);
                }}
                size={isMobile ? 'large' : 'default'}
              />
              <Button
                type="primary"
                htmlType="submit"
                form={FORM_NAME}
                label={
                  isEdit
                    ? getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.update')
                    : getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.add')
                }
                size={isMobile ? 'large' : 'default'}
              />
            </SyledFormButtonContainer>
          </VerticalFormWrapper>
        );
      }}
    </Formik>
  );
};

export { RentableItemForm };
